import { Box, Paper, Typography } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

// ── helpers ────────────────────────────────────────────────────────────────

const getMotivationMessage = (rows) => {
	if (!Array.isArray(rows) || rows.length === 0) return null;

	let best = null;
	let bestGap = Infinity;

	for (const row of rows) {
		// achieved comes back as "0"/"1" string from the API
		const achieved = Number(row.achieved) === 1;
		if (achieved) continue;

		// Never build the nudge around an opted-out agent. Their row is only here to
		// account for the network beneath them, and "keep the momentum going" reads
		// as nonsense for someone who has been removed from the portal.
		if (Number(row.is_activated ?? 1) !== 1) continue;

		const target = Number(row.target);
		const progress = Number(row.progress);
		const gap = target - progress;

		if (gap > 0 && gap < bestGap) {
			bestGap = gap;
			best = { name: row.agent_name, gap, target, progress };
		}
	}

	return best;
};

// Dynamic, motivational copy based on how close progress is to target (%).
const getProgressMessage = ({ gap, target, progress }) => {
	const pct = target > 0 ? (progress / target) * 100 : 0;

	if (pct >= 90) {
		return {
			label: "So close!",
			body: `just ${gap.toLocaleString()} more to hit the ${target.toLocaleString()} milestone — don't stop now!`,
		};
	}

	if (pct >= 70) {
		return {
			label: "Almost there!",
			body: `${gap.toLocaleString()} away from the ${target.toLocaleString()} milestone. The finish line is in sight.`,
		};
	}

	if (pct >= 40) {
		return {
			label: "Great progress!",
			body: `already over a third of the way to the ${target.toLocaleString()} milestone — keep the momentum going.`,
		};
	}

	return {
		label: "On the way!",
		body: `${progress.toLocaleString()} of ${target.toLocaleString()} so far. Every member counts toward the next milestone.`,
	};
};

// ── sub-components ────────────────────────────────────────────────────────────

export const BonusCard = ({ earnings = 0, loggedInAgent }) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center !important",
				ml: 2,
				gap: 2.5,
				p: 1.5,
			}}
		>
			<Typography
				sx={{
					fontSize: { xs: 13, sm: 15 },
					fontWeight: 800,
					color: "#1D54A2",
					letterSpacing: "0.12em",
					textTransform: "uppercase",
					whiteSpace: "nowrap",
				}}
			>
				{loggedInAgent}
			</Typography>

			<Box sx={{ width: "1.5px", height: { xs: 32, sm: 40 }, background: "#d1d5db", flexShrink: 0 }} />

			<Box>
				<Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
					total Leadership Earnings
				</Typography>
				<Typography sx={{ fontSize: 26, fontWeight: 700, color: "#1D54A2", lineHeight: 1 }}>
					${Number(earnings || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
				</Typography>
			</Box>
		</Box>
	);
};

export const MotivationMessage = ({ rows }) => {
	const msg = getMotivationMessage(rows);
	if (!msg) return null;

	const { label, body } = getProgressMessage(msg);

	return (
		<Paper
			variant="outlined"
			sx={{
				px: 2,
				py: 1.5,
				borderRadius: "0 8px 8px 0",
				borderRight: "3px solid #1D54A2",
				backgroundColor: "rgba(29,84,162,0.04)",
				display: "flex",
				gap: 3,
				alignItems: "flex-start",
			}}
		>
			<CampaignOutlinedIcon sx={{ color: "#1D54A2", mt: 0.25, fontSize: 40, flexShrink: 0 }} />
			<Box>
				<Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
					Heads up
				</Typography>
				<Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", lineHeight: 1.4 }}>
					<strong>{label}</strong> {msg.name} is {body}
				</Typography>
			</Box>
		</Paper>
	);
};
